import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Logo } from "../../components/Logo";
import { api, errDetail } from "../../lib/api";
import { useSeo } from "../../lib/seo";
import Requests from "./Requests";
import Messages from "./Messages";
import ContentAdmin from "./ContentAdmin";

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const r = await api.post("/auth/login", { email, password }); onDone(r.data); }
    catch (x) { setErr(errDetail(x, "No se pudo iniciar sesión")); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--ink)] px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-sm bg-[var(--paper)] p-8" data-testid="admin-login-form">
        <Logo />
        <h1 className="mt-8 font-display text-2xl text-[var(--ink)]">Acceso del equipo</h1>
        <p className="mt-1 text-sm text-[var(--muted)]">Solo personal autorizado.</p>
        <label className="mt-6 block text-sm">Correo electrónico<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 w-full rounded-sm border border-[var(--line)] bg-white px-4 py-3 outline-none focus:border-[var(--teal-ink)]" autoComplete="username" data-testid="admin-email-input" /></label>
        <label className="mt-4 block text-sm">Contraseña<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 w-full rounded-sm border border-[var(--line)] bg-white px-4 py-3 outline-none focus:border-[var(--teal-ink)]" autoComplete="current-password" data-testid="admin-password-input" /></label>
        {err && <p className="mt-4 text-sm text-[#B42318]" role="alert" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--ink)] py-3.5 text-sm text-white hover:bg-[var(--teal-ink)] disabled:opacity-60" data-testid="admin-login-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Entrar</button>
        <Link to="/" className="mt-6 block text-center text-sm text-[var(--muted)] underline">Volver a la web</Link>
      </form>
    </div>
  );
};

export default function AdminApp() {
  const [user, setUser] = useState(undefined);
  useSeo({ title: "Admin – Translinked", description: "", path: "/admin", lang: "es", noindex: true });
  useEffect(() => { api.get("/auth/me").then((r) => setUser(r.data)).catch(() => api.post("/auth/refresh").then(() => api.get("/auth/me")).then((r) => setUser(r.data)).catch(() => setUser(null))); }, []);
  if (user === undefined) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Login onDone={setUser} />;
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(null); };
  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-[var(--line)] bg-[var(--paper)]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-4 text-sm"><span className="hidden text-[var(--muted)] sm:inline">{user.email}</span><button onClick={logout} className="flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2 hover:border-[var(--ink)]" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Salir</button></div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 py-8">
        <Tabs defaultValue="requests">
          <TabsList className="mb-6" data-testid="admin-tabs">
            <TabsTrigger value="requests" data-testid="admin-tab-requests">Solicitudes</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Mensajes</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content">Contenido</TabsTrigger>
          </TabsList>
          <TabsContent value="requests"><Requests /></TabsContent>
          <TabsContent value="messages"><Messages /></TabsContent>
          <TabsContent value="content"><ContentAdmin /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
